"use client";

import { useRef, useState } from "react";
import { useSanityToken } from "@/app/admin/home/useSanityToken";

type Props = {
  value: string;
  onChange: (v: string) => void;
  accept: "image/*" | "video/mp4";
};

export function MediaUploadField({ value, onChange, accept }: Props) {
  const token = useSanityToken();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isVideo = accept === "video/mp4";

  const handleFile = async (file: File) => {
    if (!token) {
      setError("Sign in to Sanity Studio first.");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/admin/home/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const json = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !json.url) throw new Error(json.error ?? `Upload failed (${res.status})`);
      onChange(json.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {value ? (
        isVideo ? (
          <video src={value} controls style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 6 }} />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={value}
            alt=""
            style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", borderRadius: 6 }}
          />
        )
      ) : (
        <div style={{ fontSize: 12, color: "#6b7280" }}>No {isVideo ? "video" : "image"} yet.</div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" disabled={uploading || token === undefined} onClick={() => inputRef.current?.click()}>
          {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        {value && (
          <button type="button" disabled={uploading} onClick={() => onChange("")}>
            Remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      {error && <div style={{ fontSize: 12, color: "#b91c1c" }}>{error}</div>}
    </div>
  );
}
